import { useCallback, useRef, useState } from 'react';
import { productsService, type ProductImportPreview } from '../../../../../services/products/products.service';

// Architectural summary: owns the import modal's async/state — file -> dry-run preview -> commit.
export function useImportProductsController(
  tenantId: string,
  employeeSession: string | null,
  onImported: (result: ProductImportPreview) => void,
) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ProductImportPreview | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const committingRef = useRef(false);

  const reset = useCallback(() => {
    setFile(null);
    setPreview(null);
    setError(null);
  }, []);

  const handleFile = useCallback(
    async (picked: File | null) => {
      if (!picked || !employeeSession) {
        return;
      }
      setFile(picked);
      setPreview(null);
      setError(null);
      setIsPreviewing(true);
      try {
        setPreview(await productsService.importXlsx(tenantId, picked, employeeSession, false));
      } catch (previewError: unknown) {
        setError(previewError instanceof Error ? previewError.message : 'שגיאה בקריאת הקובץ');
      } finally {
        setIsPreviewing(false);
      }
    },
    [tenantId, employeeSession],
  );

  const confirm = useCallback(async () => {
    // Ref mutex (not the state flag) blocks a same-tick double-click before re-render (Rule 44).
    if (!file || !employeeSession || !preview?.canCommit || committingRef.current) {
      return;
    }
    committingRef.current = true;
    setIsCommitting(true);
    setError(null);
    try {
      const result = await productsService.importXlsx(tenantId, file, employeeSession, true);
      onImported(result);
      reset();
    } catch (commitError: unknown) {
      setError(commitError instanceof Error ? commitError.message : 'שגיאה בייבוא');
    } finally {
      committingRef.current = false;
      setIsCommitting(false);
    }
  }, [file, employeeSession, preview, tenantId, onImported, reset]);

  return { file, preview, isPreviewing, isCommitting, error, handleFile, confirm, reset };
}
